<template>
    <div v-if="config.display" :style="{'width': config.width || '100%', 'display': config.inlineLabel ? 'flex' : 'block'}" class="control e-control">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="asField" :style="{'flex-grow': config.inlineLabel ? '9' : false}">
            <display-value :id="config.id" :value="value"></display-value>
        </div>
        <div v-else-if="config.ENABLE_HTML" :style="{'flex-grow': config.inlineLabel ? '9' : false}">
            <ejs-richtexteditor
                v-bind="syncfusionConfig"
                :enterKey="'DIV'"
                v-model="formattedValue"
                v-on:change="action($event, 'onchange')"
                v-on:input="action($event, 'oninput')"
                v-on:focus="action($event, 'onfocus'); keyboardShow($event);"
                :created="created"
                :ref="config.ref || config.id"
                :readonly="config.disabled"
                :data-test-id="dataTestId"
                :htmlAttributes="config.htmlAttributes"
                :toolbarSettings="config.toolbarSettings || toolbarSettings"
                :placeholder="ddStoreFilter(config.placeholder)"
            ></ejs-richtexteditor>
        </div>
        <div v-else :style="{'flex-grow': config.inlineLabel ? '9' : false}" style="position: relative;">
            <div v-if="config.showExpandMinimizeBtns && config.type === 'text' && value" class="textarea-btn_div">
                <div>
                    <button title="Expand" type="button" :disabled="expanded" v-click="expanded" @click="expandTextArea();"
                            class="textarea-btn expand-btn"><span class="sr-only">expand</span>Expand
                    </button>
                    <button title="Minimize" type="button" :disabled="!expanded" v-click="!expanded" @click="minimizeTextArea();"
                            class="textarea-btn close-btn"><span class="sr-only">minimize</span>Minimize
                    </button>
                </div>
            </div>
            <div v-if="config.mask">
                <ejs-maskedtextbox :class="config.type === 'text' ? 'text-area' : false"
                             v-bind="syncfusionConfig"
                             v-model="value"
                             :type="config.type === 'password' ? 'password' : 'text'"
                             :mask="config.customMask && config.mask === 'custom' ? config.customMask : config.mask"
                             v-on:change="action($event, 'onchange')"
                             v-on:input="action($event, 'oninput')"
                             v-on:focus="action($event, 'onfocus'); keyboardShow($event);"
                             :autocomplete="config.autocomplete || 'off'"
                             :multiline="config.type === 'text'"
                             :input="inputHandler"
                             :created="created"
                             :ref="config.ref || config.id"
                             :enabled="!config.disabled"
                             :data-test-id="dataTestId"
                             :htmlAttributes="config.htmlAttributes"
                             :placeholder="ddStoreFilter(config.placeholder)"
                ></ejs-maskedtextbox>
            </div>
            <div v-else>
                <ejs-textbox :class="config.type === 'text' ? 'text-area' : false"
                             v-bind="syncfusionConfig"
                             v-model="formattedValue"
                             :type="config.type === 'password' ? 'password' : 'text'"
                             v-on:change="action($event, 'onchange')"
                             v-on:input="action($event, 'oninput')"
                             v-on:focus="action($event, 'onfocus'); keyboardShow($event);"
                             :autocomplete="config.autocomplete || 'off'"
                             :multiline="config.type === 'text'"
                             :input="inputHandler"
                             :created="created"
                             :ref="config.ref || config.id"
                             :enabled="!config.disabled"
                             :data-test-id="dataTestId"
                             :htmlAttributes="config.htmlAttributes"
                             :placeholder="ddStoreFilter(config.placeholder)"
                ></ejs-textbox>
            </div>
            <p v-if="config.message && !config.hideMessage" class="input-info" v-html="ddStoreFilter(config.message)"></p>
            <vue-touch-keyboard id="keyboard" v-if="keyboardVisible" :layout="keyboardLayout" :input="keyboardInput" :cancel="keyboardHide" :accept="keyboardAccept"/>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import VueTouchKeyboard from "@common/keyboard/keyboard";
import { library } from "@common/library/library";
import { controlMixins } from "./controlMixins.js";

let editorSettings = {
    disableTrim: {
        type: 'boolean',
        label: 'Disable Trim White Spaces',
        default: false
    },
    onchange: {
        type: 'actionfield',
        label: 'Change Action',
        itemsSource: 'actionStore.getActionOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        default: {
            action: '',
            params: {}
        }
    },
    keyboardLayout: {
        type: "dropdown",
        label: "On-Screen Keyboard layout",
        default: "none",
        items: [
            { text: "None", value: "none" },
            { text: "Normal", value: "normal" },
            { text: "Compact", value: "compact" },
            { text: "Numeric", value: "numeric" },
        ],
        fields: { text: 'text', value: 'value' }
    }
};

const keyboardLayouts = {
    "normal": {
        _meta: {
            "tab": {
                key: "\t", text: "Tab", width: 60, classes: "control"
            },
            "shiftl": {
                keySet: "shifted", text: "Shift", width: 100, classes: "control"
            },
            "shiftr": {
                keySet: "shifted", text: "Shift", width: 100, classes: "control"
            },
            "caps": {
                keySet: "capsed", text: "Caps lock", width: 80, classes: "control"
            },
            "space": { key: " ", text: "Space", width: 180 },
            "enter": {
                key: "\r\n", text: "Enter", width: 80, classes: "control"
            },
            "backspace": { func: "backspace", classes: "control backspace", width: 65 },
            "accept": { func: "accept", text: "Close", classes: "control featured" },
        },

        default: [
            "` 1 2 3 4 5 6 7 8 9 0 - = {backspace}",
            "{tab} q w e r t y u i o p [ ] \\",
            "{caps} a s d f g h j k l ; ' {enter}",
            "{shiftl} z x c v b n m , . / {shiftr}",
            "{space} {accept}"
        ],
        shifted: [
            "~ ! @ # $ % ^ & * ( ) _ + {backspace}",
            "{tab} Q W E R T Y U I O P { } |",
            "{caps} A S D F G H J K L : \" {enter}",
            "{shiftl} Z X C V B N M < > ? {shiftr}",
            "{space} {accept}"
        ],
        capsed: [
            "` 1 2 3 4 5 6 7 8 9 0 - = {backspace}",
            "{tab} Q W E R T Y U I O P [ ] \\",
            "{caps} A S D F G H J K L ; ' {enter}",
            "{shiftl} Z X C V B N M , . / {shiftr}",
            "{space} {accept}"
        ]
    },

    "compact": {
        _meta: {
            "default": { keySet: "default", text: "abc", classes: "control" },
            "alpha": { keySet: "default", text: "Abc", classes: "control" },
            "shift": { keySet: "shifted", text: "ABC", classes: "control" },
            "numbers": { keySet: "numbers", text: "123", classes: "control" },
            "space": { key: " ", text: "Space", width: 200 },
            "backspace": { func: "backspace", classes: "control" },
            "accept": { func: "accept", text: "Close", classes: "control featured" },
            "zero": { key: "0", width: 130 }
        },

        default: [
            "q w e r t y u i o p",
            " a s d f g h j k l ",
            "{shift} z x c v b n m {backspace}",
            "{numbers} , {space} . {accept}"
        ],

        shifted: [
            "Q W E R T Y U I O P",
            " A S D F G H J K L ",
            "{default} Z X C V B N M ",
            "{numbers} _ {space} {backspace} {accept}"
        ],

        numbers: [
            "1 2 3",
            "4 5 6",
            "7 8 9",
            "  {alpha} . {zero} {backspace} {accept}"
        ]
    },

    "numeric": {
        _meta: {
            "backspace": { func: "backspace", classes: "control" },
            "accept": { func: "accept", text: "Close", classes: "control featured" },
            "zero": { key: "0", width: 130 }
        },

        default: [
            "1 2 3",
            "4 5 6",
            "7 8 9",
            "{zero} {backspace} {accept}"
        ]
    }
};

const textComponent = {
    editorSettings: editorSettings,
    data: function () {
        return {
            keyboardInput: null,
            keyboardVisible: false,
            keyboardLayout: 'none',
            expanded: false,
            toolbarSettings: {
                items: [
                    'Bold',
                    'Italic',
                    'Underline',
                    '|',
                    'Formats',
                    'Alignments',
                    'OrderedList',
                    'UnorderedList',
                    '|',
                    'CreateLink',
                    'Image',
                    '|',
                    'SourceCode',
                    'clearFormat',
                    'Undo',
                    'Redo'
                ]
            }
        };
    },
    mixins: [controlMixins],
    computed: {
        formattedValue: {
            get() {
                let value = this.value;
                if (!this.config.disableTrim && typeof value === 'string') {
                    value = value.trim();
                }
                return value;
            },
            set(value) {
                if (!this.config.disableTrim && typeof value === 'string') {
                    value = value.trim();
                }
                this.value = value;
            }
        },
        ref() {
            return this.config.ref || this.config.id;
        }
    },
    updated() {
        this.addKeyUpHandler();
    },
    methods: {
        addKeyUpHandler() {
            if (typeof this?.$refs?.[this.ref]?.ej2Instances?.element?.parentElement !== 'undefined') {
                this.$refs[this.ref].ej2Instances.element.parentElement.removeEventListener('keyup', this.keyup, true);
                this.$refs[this.ref].ej2Instances.element.parentElement.addEventListener('keyup', this.keyup, true);
            }
        },
        keyup(ev) {
            if (ev.keyCode === 13) { // enter
                if (typeof this.$parent.processAction === 'function') { // NOSONAR
                    this.$parent.processAction('trapEnter', ev);
                } else if (this.$parent.component && typeof this.$parent.component.processAction === 'function') {
                    this.$parent.component.processAction('trapEnter', ev);
                }
            }
        },
        keyboardAccept() {
            this.keyboardHide();
        },
        keyboardShow(e) {
            this.keyboardInput = e.event.target;
            if (this.config && this.config.keyboardLayout && this.config.keyboardLayout !== 'none' && !library.isMobile()) {
                this.keyboardLayout = keyboardLayouts[this.config.keyboardLayout];
                this.keyboardVisible = true;
            }
        },
        keyboardHide() {
            this.keyboardVisible = false;
        },
        created: function () {
            if (this.config) {
                const ref = this.config.ref || this.config.id;
                if (ref && !this.config.ENABLE_HTML) {
                    this.addKeyUpHandler();
                    if (this.config.type === 'text') {
                        if (this.config.rows) this.$refs[ref].ej2Instances.textarea.rows = parseInt(this.config.rows, 10);
                        else {
                            this.$refs[ref].ej2Instances.textarea.rows = 3;
                            this.$refs[ref].ej2Instances.textarea.style.height = 'auto';
                        }
                    }
                }
            }
        },
        inputHandler: (args) => {
            if (args.event.target.rows >= 3) {
                args.event.currentTarget.style.height = 'auto';
                args.event.currentTarget.style.height = `${args.event.currentTarget.scrollHeight}px`;
            }
        },
        expandTextArea() {
            const ref = this.config.ref || this.config.id;
            this.expanded = true;
            if (ref) {
                this.$refs[ref].ej2Instances.textarea.style.height = 'auto';
                this.$refs[ref].ej2Instances.textarea.style.height = `${this.$refs[ref].ej2Instances.textarea.scrollHeight}px`;
            }
            this.goToEndOfTextArea();
            return this.expanded;
        },
        minimizeTextArea() {
            this.expanded = false;
            const ref = this.config.ref || this.config.id;
            this.$refs[ref].ej2Instances.textarea.style.height = '54px';
        },
        goToEndOfTextArea() {
            const ref = this.config.ref || this.config.id;
            let charCount = this.$refs[ref].ej2Instances.textarea.value.length;
            if (ref) {
                this.$refs[ref].ej2Instances.textarea.style.height = 'auto';
                this.$refs[ref].ej2Instances.textarea.style.height = `${ this.$refs[ref].ej2Instances.textarea.scrollHeight }px`;
                this.$refs[ref].ej2Instances.textarea.focus();
                this.$refs[ref].ej2Instances.textarea.setSelectionRange(charCount, charCount);
            }
        },
    },
    components: {
        'ejs-textbox': defineAsyncComponent(() => import('@common/syncfusion/text')),
        'ejs-maskedtextbox': defineAsyncComponent(() => import('@common/syncfusion/maskedText')),
        'vue-touch-keyboard': VueTouchKeyboard,
        'ejs-richtexteditor': defineAsyncComponent(() => import('@common/syncfusion/richtext')),
    }
};

export { textComponent };
export default textComponent;
</script>
<style scoped>
@import '~vue-touch-keyboard/dist/vue-touch-keyboard.css';

.textarea-btn_div {
    display: flex;
    justify-content: flex-end;
    margin-top: -2rem;
}

.textarea-btn_div .hr-line {
    border-top: 1px solid #e3e3e3;
}

button.textarea-btn {
    background: #fff;
    border: 1px solid #d0d0d0;
    font-size: 0.9rem;
    padding: 0.25rem 0.5rem;
    letter-spacing: .1px;
}

button.textarea-btn:last-child {
    border-left: none;
}

button.textarea-btn:hover {
    background: #e7e7e7;
}

button.textarea-btn:hover:disabled,
button.textarea-btn:disabled {
    background: #f6f6f6;
}

.text-input {
    margin-bottom: 30px;
}

.input-info {
    color: #2973b7;
}

.e-float-input, .e-float-input.e-control-wrapper {
    margin-top: 0;
}

:deep(#keyboard) {
    max-width: 100% !important;
}

:deep(.e-toolbar-wrapper) {
    height: 10% !important;
    max-height: 43px !important;
}

:deep(.e-rte-content) {
    height: 90% !important;
}
</style>
